import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";

const statusColor = (status) => {
  switch (status) {
    case "active":
    case "completed":
    case "paid":
      return "success";
    case "trial":
      return "info";
    case "pending":
    case "trial_ended_pending":
      return "warning";
    case "expired":
    case "cancelled":
      return "default";
    default:
      return "default";
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatPaymentMethod = (method) => {
  if (!method) return "—";
  if (method === "bank_transfer") return "Bank transfer";
  if (method === "stripe") return "Stripe";
  return method;
};

const formatPlatform = (platform) => {
  if (!platform) return "—";
  const value = String(platform).toLowerCase();
  if (value === "ios") return "iOS";
  if (value === "android") return "Android";
  return platform;
};

const formatMoney = (pence, currency = "gbp") => {
  const amount = Number(pence || 0) / 100;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
};

const MobileUsers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await api.get("admin/mobile-users", {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: search.trim() || undefined,
        },
      });
      if (res.data?.success) {
        const payload = res.data.data || {};
        setRows(payload.data || []);
        setTotal(payload.total || 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load mobile users.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchRows();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <PhoneIphoneIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Mobile app users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Student accounts, subscriptions, payment history, and admin billing actions.
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, pet name, or user id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Next billing</TableCell>
                  <TableCell>Last payment</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      No mobile users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.full_name || "—"}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{formatPlatform(row.platform)}</TableCell>
                      <TableCell>{row.country || "—"}</TableCell>
                      <TableCell>{row.plan_name || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.subscription_status || "none"}
                          color={statusColor(row.subscription_status)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(row.next_billing_date)}</TableCell>
                      <TableCell>
                        {row.last_payment_status ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Chip
                              size="small"
                              label={row.last_payment_status}
                              color={statusColor(row.last_payment_status)}
                              sx={{ width: "fit-content" }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(row.last_payment_paid_at)}
                            </Typography>
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{formatPaymentMethod(row.last_payment_method)}</TableCell>
                      <TableCell>
                        {row.last_payment_amount_pence != null
                          ? formatMoney(row.last_payment_amount_pence, row.last_payment_currency)
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/admin/mobile-users/${row.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MobileUsers;
